import { useState, useEffect, useCallback } from 'react';
import { EventComment } from '../services/Types';
import { getEventComments, createEventComment } from '../services/WorkflowService';

interface EventCommentsResponse {
  eventComments: {
    statusCode: number;
    data: EventComment[];
    message?: string;
    error?: string;
  };
}

interface CreateEventCommentResponse {
  createEventComment: {
    statusCode: number;
    data: EventComment;
    message?: string;
    error?: string;
  };
}

export const useEventComments = (eventId: number) => {
  const [comments, setComments] = useState<EventComment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const fetchComments = useCallback(async () => {
    if (!eventId) return;

    setLoading(true);
    setError(null);
    try {
      const result = await getEventComments(eventId);
      const data = result.data as EventCommentsResponse | undefined;
      if (data?.eventComments.statusCode === 200 && data.eventComments.data) {
        setComments(data.eventComments.data);
      } else {
        setError(data?.eventComments.error || data?.eventComments.message || 'Failed to load comments');
        setComments([]);
      }
    } catch (err) {
      console.error('Error getting event comments:', err);
      setError('Failed to load comments');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (eventId) {
      fetchComments();
    }
  }, [eventId, fetchComments]);

  const sendComment = async (message: string): Promise<boolean> => {
    if (!eventId || !message.trim()) return false;

    setSending(true);
    setError(null);
    try {
      const result = await createEventComment(eventId, message);
      const data = result.data as CreateEventCommentResponse | undefined;
      if (data?.createEventComment.statusCode === 200 && data.createEventComment.data) {
        setComments(prev => [...prev, data.createEventComment.data]);
        return true;
      }
      setError(data?.createEventComment.error || data?.createEventComment.message || 'Failed to send comment');
      return false;
    } catch (err) {
      console.error('Error sending comment:', err);
      setError('Failed to send comment');
      return false;
    } finally {
      setSending(false);
    }
  };

  return { comments, loading, error, sending, fetchComments, sendComment };
};
